(function () {
  'use strict';
  if (customElements.get('gs-retreat-passport')) return;
  const escape = value => String(value ?? '').replace(/[&<>"']/g, c => ({'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'}[c]));
  const initials = name => name.split(/\s+/).slice(0, 2).map(s => s[0]).join('').toUpperCase();
  const destinationName = place => [place.label, place.country].filter(Boolean).join(', ');
  const badge = name => '<span class="badge"><span aria-hidden="true">✦</span> ' + escape(name) + '</span>';
  const starPoints = (x, y, outer, inner, tips) => Array.from({length: tips * 2}, (_, i) => {
    const angle = Math.PI * i / tips - Math.PI / 2;
    const radius = i % 2 ? inner : outer;
    return [(x + Math.cos(angle) * radius).toFixed(2), (y + Math.sin(angle) * radius).toFixed(2)].join(',');
  }).join(' ');
  const sealEdge = starPoints(88, 84, 77, 72, 28);
  const patchLines = value => {
    const lines = [];
    String(value).split(/\s+/).forEach(word => {
      const parts = word.match(/.{1,14}/gu) || [];
      parts.forEach(part => {
        const last = lines.length - 1;
        if (last >= 0 && lines[last].length + part.length + 1 <= 14) lines[last] += ' ' + part;
        else lines.push(part);
      });
    });
    return lines.length > 3 ? [...lines.slice(0, 2), lines[2].slice(0, 11) + '…'] : lines;
  };
  const fallbackCopy = {
    loading: 'Loading the retreat leaderboard…',
    notPublishedTitle: 'Our passports are getting ready.',
    notPublishedText: 'The leaderboard will appear here once it is published.',
    errorTitle: 'The leaderboard is taking a moment.',
    errorText: 'Please try again.',
    errorRetry: 'Reload page'
  };
  const safeLink = (value, fallback) => {
    try { const url = new URL(value || fallback, window.location.href); return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : fallback; }
    catch (_) { return fallback; }
  };

  class GSPassport extends HTMLElement {
    constructor() {
      super();
      this.attachShadow({mode: 'open'});
      this.query = '';
      this.group = 'all';
      this.limit = 20;
      this.shadowRoot.addEventListener('click', event => this.onClick(event));
      this.shadowRoot.addEventListener('input', event => {
        if (event.target.id === 'traveler-search') {
          this.query = event.target.value;
          this.limit = 20;
          this.renderBoard({announceDelay: 400});
        }
      });
    }
    connectedCallback() {
      if (!this.data) this.load();
    }
    disconnectedCallback() {
      clearTimeout(this.announceTimer);
      const dialog = this.shadowRoot.querySelector('dialog');
      if (dialog && dialog.open) dialog.close();
    }
    styleLink() {
      return '<link rel="stylesheet" href="' + escape(this.getAttribute('css-url')) + '">';
    }
    load() {
      try { this.copy = JSON.parse(this.querySelector('script[data-gs-passport-copy]').textContent); }
      catch (_) { this.copy = {}; }
      this.shadowRoot.innerHTML = this.styleLink() + `<div class="load-state" role="status">${this.translated('loading')}</div>`;
      try {
        if (!window.GSPassportModel) throw new Error('Passport model unavailable');
        const inline = this.querySelector('script[data-gs-passport-feed]');
        if (!inline || !inline.textContent.trim()) {
          this.shadowRoot.innerHTML = this.styleLink() + `<div class="load-state"><h2>${this.translated('notPublishedTitle')}</h2><p>${this.translated('notPublishedText')}</p></div>`;
          return;
        }
        const payload = JSON.parse(inline.textContent);
        this.data = window.GSPassportModel.normalize(payload);
        if (this.data.bookingIssues.length) console.warn('GS Passport: some upcoming trips could not be displayed. Earned history and ranks are unchanged.', this.data.bookingIssues);
        this.render();
      } catch (error) {
        console.error('GS Passport could not be displayed.', error);
        this.shadowRoot.innerHTML = this.styleLink() + `<div class="load-state"><h2>${this.translated('errorTitle')}</h2><p>${this.translated('errorText')}</p><button class="secondary" data-action="retry">${this.translated('errorRetry')}</button></div>`;
      }
    }
    text(key, replacements = {}) {
      let value = this.copy[key] || fallbackCopy[key] || '';
      Object.entries(replacements).forEach(([name, replacement]) => { value = value.split('__' + name.toUpperCase() + '__').join(String(replacement)); });
      return value;
    }
    translated(key, replacements = {}) {
      return escape(this.text(key, replacements));
    }
    plural(key, count, replacements = {}) {
      return this.text(key + (count === 1 ? 'One' : 'Many'), {count, ...replacements});
    }
    render() {
      const {participants, milestones, completedRetreats} = this.data;
      this.retreatsUrl = safeLink(this.getAttribute('retreats-url'), 'https://retreats.grownstrong.com/');
      this.clubUrl = safeLink(this.getAttribute('club-url'), 'https://retreats.grownstrong.com/pages/1-percent-club');
      const clubCount = participants.filter(p => p.club).length;
      this.shadowRoot.innerHTML = this.styleLink() + `
        <div class="wrap">
          ${this.data.bookingIssues.length ? `<p class="data-notice" role="status">${this.translated('bookingsWarning')}</p>` : ''}
          <section aria-label="${this.translated('topParticipants')}" ${participants.length ? '' : 'hidden'}>
            <div class="podium">${participants.slice(0, 3).map((p, i) => this.podiumCard(p, i)).join('')}</div>
          </section>
          <div class="community" aria-label="${this.translated('communityLabel')}">
            <div class="stat"><b>${participants.length}</b><span>${this.translated('travelersStat')}</span></div>
            ${completedRetreats === null ? '' : `<div class="stat"><b>${completedRetreats}</b><span>${this.translated('retreatsCompleted')}</span></div>`}
            <div class="stat"><b>${clubCount}</b><span>${this.translated('clubMembers', {club: milestones.clubName})}</span></div>
          </div>
          <section aria-labelledby="leaderboard-heading">
            <div class="board-top"><h2 id="leaderboard-heading">${this.translated('leaderboardHeading')}</h2>
              <div class="tabs" role="group" aria-label="${this.translated('groupLabel')}">
                <button data-action="all" aria-pressed="true">${this.translated('everyone')}</button>
                <button data-action="club" aria-pressed="false">${escape(milestones.clubName)} <span aria-hidden="true">✦</span></button>
                <button data-action="reunion" aria-pressed="false">${this.translated('reunionFilter')}</button>
              </div>
            </div>
            <div class="filters">
              <div class="search"><label class="sr-only" for="traveler-search">${this.translated('searchLabel')}</label>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><circle cx="10.5" cy="10.5" r="6.5"/><path d="m16 16 5 5"/></svg>
                <input type="search" id="traveler-search" placeholder="${this.translated('searchPlaceholder')}" autocomplete="off" spellcheck="false">
              </div>
            </div>
            <div class="count-line"><span id="result-count"></span><span id="result-announcement" class="sr-only" role="status" aria-live="polite" aria-atomic="true"></span><button class="reset" data-action="reset" hidden>${this.translated('clearFilters')}</button></div>
            <div class="board">
              <div class="table-head" aria-hidden="true"><span>${this.translated('headRank')}</span><span>${this.translated('headTraveler')}</span><span class="total-head">${this.translated('headRetreats')}</span><span class="view-head"></span></div>
              <ul class="rows" aria-label="${this.translated('rankingsLabel')}"></ul>
              <div class="empty" hidden><h3>${this.translated('noResults')}</h3><p>${this.translated('emptySearch')}</p><button class="secondary" data-action="reset">${this.translated('clearFilters')}</button></div>
            </div>
            <div class="pagination"><button class="secondary" data-action="more">${this.translated('showMore')}</button><span id="page-count"></span></div>
          </section>
          <aside class="club-callout">
            <div class="club-seal" aria-hidden="true"><b>1%</b><span>CLUB</span></div>
            <div class="club-text"><h2>${this.translated('clubHeading', {count: milestones.startsAt})}</h2><p>${this.translated('clubIntro', {count: milestones.startsAt, club: milestones.clubName})}</p></div>
            <a class="text-link" href="${escape(this.clubUrl)}">${this.translated('meetClub', {club: milestones.clubName})}</a>
          </aside>
          <details class="rules"><summary>${this.translated('rulesSummary')}</summary><p>${this.translated('rules', {club: milestones.clubName})}</p></details>
        </div>
        <dialog aria-labelledby="passport-name"><div id="passport-content"></div></dialog>`;
      this.shadowRoot.querySelector('dialog').addEventListener('close', () => {
        if (this.opener && this.opener.isConnected) this.opener.focus();
      });
      this.renderBoard();
    }
    milestoneSeal(earned) {
      const level = Math.max(0, Math.round((earned.count - this.data.milestones.startsAt) / this.data.milestones.interval));
      const palette = [
        {face: '#fcf5e5', ink: '#584321', ribbon: '#c99a48'},
        {face: '#173f43', ink: '#faecd0', ribbon: '#173f43'},
        {face: '#00b0b9', ink: '#073b3e', ribbon: '#c99a48'},
        {face: '#203647', ink: '#faecd0', ribbon: '#203647'}
      ][Math.min(level, 3)];
      const stars = Math.min(level + 1, 5);
      const center = level === 0 ? '1%' : String(earned.count);
      const top = level === 0 ? 'GROWN STRONG' : this.data.milestones.clubName.toUpperCase();
      const bottom = escape(((level === 0 ? earned.count + ' ' : '') + (this.copy.retreatsLabel || '')).toUpperCase());
      return `<svg class="collectible-seal milestone-seal" data-milestone="${earned.count}" viewBox="0 0 176 190" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" font-family="Poppins, Arial, sans-serif" text-anchor="middle">
        <path d="M44 131 31 184 57 170 76 187 87 142 100 187 119 170 145 184 132 131Z" fill="${palette.ribbon}" stroke="#b78939" stroke-width="1.5"/>
        <polygon points="${sealEdge}" fill="#d6b36b" stroke="#a57b32" stroke-width="1"/>
        <circle cx="88" cy="84" r="65" fill="${palette.face}" stroke="#f9e9bf" stroke-width="2"/>
        <circle cx="88" cy="84" r="58" fill="none" stroke="#b8934d" stroke-width="1"/>
        <text x="88" y="${level === 0 ? 53 : 48}" fill="${palette.ink}" font-size="${level === 0 ? 8 : 9}" font-weight="700" letter-spacing="1.2" ${level === 0 ? 'textLength="74" lengthAdjust="spacingAndGlyphs"' : top.length > 18 ? 'textLength="88" lengthAdjust="spacingAndGlyphs"' : ''}>${escape(top)}</text>
        <text x="88" y="98" fill="${palette.ink}" font-size="${center.length > 2 ? 37 : 46}" font-weight="800" letter-spacing="-2">${escape(center)}</text>
        <text x="88" y="116" fill="${palette.ink}" font-size="9" font-weight="700" letter-spacing="1">${bottom}</text>
        ${Array.from({length: stars}, (_, index) => `<polygon points="${starPoints(88 + (index - (stars - 1) / 2) * 14, 132, 4.5, 2, 5)}" fill="${palette.ink}"/>`).join('')}
      </svg>`;
    }
    reunionSeal(reunion) {
      const palettes = [{ink: '#155459', paper: '#e4f2ef', accent: '#d6b36b'}, {ink: '#7e4536', paper: '#fbede0', accent: '#d6a467'}, {ink: '#344b64', paper: '#eaf0f6', accent: '#c6a66b'}];
      const index = Array.from(reunion.id).reduce((n, c) => n + c.charCodeAt(0), 0) % palettes.length;
      const {ink, paper, accent} = palettes[index];
      const place = reunion.label.replace(/\breunion\b/gi, '').trim() || reunion.label;
      const lines = patchLines(place);
      return `<svg class="collectible-seal reunion-seal" data-reunion-id="${escape(reunion.id)}" viewBox="0 0 176 190" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" font-family="Poppins, Arial, sans-serif" text-anchor="middle">
        <path d="M23 27Q88 -4 153 27V151Q88 188 23 151Z" fill="${paper}" stroke="${ink}" stroke-width="4"/>
        <path d="M31 33Q88 6 145 33V146Q88 177 31 146Z" fill="none" stroke="${ink}" stroke-width="1" stroke-dasharray="3 3"/>
        <path d="M32 33Q88 8 144 33V52H32Z" fill="${ink}"/>
        <text x="88" y="40" fill="#fff" font-size="10" font-weight="700" letter-spacing="1.5">${escape((this.copy.reunionSeal || '').toUpperCase())}</text>
        <polygon points="${starPoints(88, 70, 13, 4, 4)}" fill="${ink}"/>
        <circle cx="61" cy="70" r="2" fill="${accent}"/><circle cx="115" cy="70" r="2" fill="${accent}"/>
        ${lines.map((line, i) => `<text x="88" y="${lines.length === 1 ? 111 : 100 + i * 16}" fill="${ink}" font-size="${lines.length > 2 ? 12 : 15}" font-weight="800" letter-spacing=".3">${escape(line.toUpperCase())}</text>`).join('')}
        ${reunion.year === null ? '' : `<text x="88" y="147" fill="${ink}" font-size="21" font-weight="700" letter-spacing="1">${reunion.year}</text>`}
      </svg>`;
    }
    awardPreview(p) {
      const club = p.badges[p.badges.length - 1];
      const reunion = p.reunions[p.reunions.length - 1];
      return club || reunion ? `<span class="row-emblems" aria-hidden="true">${club ? this.milestoneSeal(club) : ''}${reunion ? this.reunionSeal(reunion) : ''}</span>` : '';
    }
    rankText(p) {
      return this.text(p.tied ? 'rankSummaryTied' : 'rankSummary', {rank: p.rank});
    }
    alumniText(p) {
      return this.translated(p.total === 0 && p.upcoming.length ? 'firstRetreatAhead' : 'retreatAlumni');
    }
    podiumCard(p, i) {
      const earned = p.badges[p.badges.length - 1];
      const reunion = p.reunions[p.reunions.length - 1];
      const seal = earned ? this.milestoneSeal(earned) : reunion ? this.reunionSeal(reunion) : '';
      return `<button class="pod-card ${i === 0 ? 'leading' : ''}" data-action="passport" data-id="${escape(p.id)}" aria-label="${this.translated('podiumAria', {name: p.name, retreats: this.plural('completedRetreats', p.total), rank: this.rankText(p)})}">
        <span class="pod-top"><span class="pod-avatar" aria-hidden="true">${escape(initials(p.name))}</span><span class="rank-label">${this.translated(p.tied ? 'rankLabelTied' : 'rankLabel', {rank: p.rank})}</span></span>
        <span class="pod-main"><span class="pod-summary"><span class="pod-name">${escape(p.name)}</span><span class="pod-count"><b>${p.total}</b><span>${this.translated('retreatsTogether')}</span></span></span>${seal ? `<span class="pod-emblem" aria-hidden="true">${seal}</span>` : ''}</span>
        <span class="pod-bottom">${p.club ? badge(p.badge) : `<span>${this.alumniText(p)}</span>`}<span class="view">${this.translated('viewPassport')}</span></span>
      </button>`;
    }
    renderBoard({announceDelay = 0} = {}) {
      if (!this.data) return;
      const people = window.GSPassportModel.filter(this.data.participants, {query: this.query, club: this.group === 'club', reunion: this.group === 'reunion'});
      const displayed = people.slice(0, this.limit);
      this.shadowRoot.querySelector('.rows').innerHTML = displayed.map(p => {
        const places = p.trips.slice(0, 2).map(t => `<span class="destination-pill">${escape(destinationName(t))}${t.count > 1 ? ' ×' + t.count : ''}</span>`).join('');
        const more = p.trips.length > 2 ? `<span class="more-destinations">${this.translated('moreDestinations', {count: p.trips.length - 2})}</span>` : '';
        return `<li><button class="row" data-action="passport" data-id="${escape(p.id)}" aria-label="${this.translated('rowAria', {name: p.name, rank: this.rankText(p), retreats: this.plural('completedRetreats', p.total)})}">
          <span class="position"><b>${p.rank}</b>${p.tied ? '<span class="tie">=</span>' : ''}</span>
          <span class="traveler"><span class="avatar" aria-hidden="true">${escape(initials(p.name))}</span><span class="traveler-details"><span class="traveler-name">${escape(p.name)}</span>${p.club ? badge(p.badge) : ''}${this.awardPreview(p)}${places ? `<span class="trip-summary">${places}${more}</span>` : ''}</span></span>
          <span class="trip-total">${p.total}<span class="mobile-label">${escape(this.plural('retreatUnit', p.total))}</span></span>
          <span class="view-row">${this.translated('viewPassport')}</span>
        </button></li>`;
      }).join('');
      const filtered = this.group !== 'all' || this.query.trim();
      const count = this.plural(filtered ? 'resultFiltered' : 'resultRanked', people.length);
      this.shadowRoot.querySelector('#result-count').textContent = count;
      // Screen readers hear the count once typing pauses, not on every keystroke.
      clearTimeout(this.announceTimer);
      const announce = () => { const region = this.shadowRoot.querySelector('#result-announcement'); if (region) region.textContent = count; };
      if (announceDelay) this.announceTimer = setTimeout(announce, announceDelay);
      else announce();
      this.shadowRoot.querySelector('#page-count').textContent = this.text('pageCount', {shown: displayed.length, total: people.length});
      this.shadowRoot.querySelector('.empty').hidden = people.length > 0;
      this.shadowRoot.querySelector('.table-head').hidden = people.length === 0;
      this.shadowRoot.querySelector('[data-action="more"]').hidden = displayed.length >= people.length;
      this.shadowRoot.querySelector('.pagination').hidden = people.length === 0;
      this.shadowRoot.querySelector('.count-line .reset').hidden = !this.query && this.group === 'all';
      ['all', 'club', 'reunion'].forEach(group => {
        this.shadowRoot.querySelector(`[data-action="${group}"]`).setAttribute('aria-pressed', String(this.group === group));
      });
    }
    onClick(event) {
      const dialog = this.shadowRoot.querySelector('dialog');
      if (event.target === dialog) {
        const box = dialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) dialog.close();
        return;
      }
      const button = event.target.closest('[data-action]');
      if (!button) return;
      switch (button.dataset.action) {
        case 'retry': window.location.reload(); break;
        case 'passport': this.openPassport(button.dataset.id, button); break;
        case 'close': dialog.close(); break;
        case 'more': {
          const previousCount = this.shadowRoot.querySelectorAll('.rows .row').length;
          this.limit += 20;
          this.renderBoard();
          const next = this.shadowRoot.querySelectorAll('.rows .row')[previousCount];
          if (next) next.focus({preventScroll: true});
          break;
        }
        case 'all':
        case 'club':
        case 'reunion': this.group = button.dataset.action; this.limit = 20; this.renderBoard(); break;
        case 'reset':
          this.query = ''; this.group = 'all'; this.limit = 20;
          this.shadowRoot.querySelector('#traveler-search').value = '';
          this.renderBoard();
          this.shadowRoot.querySelector('#traveler-search').focus();
          break;
      }
    }
    openPassport(id, opener) {
      const p = this.data.participants.find(person => person.id === id);
      if (!p) return;
      this.opener = opener;
      const milestone = p;
      const club = this.data.milestones.clubName;
      const progressTitle = !p.club ? this.text('progressTitleClub', {club}) : this.text('progressTitleNext');
      const progressText = escape(this.plural(p.club ? 'progressToNext' : 'progressToClub', milestone.remaining, {club, next: milestone.next}));
      this.shadowRoot.querySelector('#passport-content').innerHTML = `
        <div class="passport-cover"><button class="close" data-action="close" aria-label="${this.translated('closePassport')}" autofocus>×</button><p class="eyebrow">${this.translated('passportEyebrow')}</p>
          <div class="passport-person"><span class="pod-avatar" aria-hidden="true">${escape(initials(p.name))}</span><div><h2 id="passport-name">${escape(p.name)}</h2>${p.club ? badge(p.badge) : `<span class="passport-alumni">${this.alumniText(p)}</span>`}</div></div>
        </div>
        <div class="passport-body">
          ${p.upcomingIncomplete ? `<p class="data-notice" role="status">${this.translated('passportBookingsWarning')}</p>` : ''}
          <div class="passport-stats"><div><b>${p.total}</b><span>${this.translated('retreatsLabel')}</span></div><div><b>#${p.rank}${p.tied ? ' =' : ''}</b><span>${this.translated(p.tied ? 'allTimeRankTied' : 'allTimeRank')}</span></div></div>
          <div class="progress-area"><div class="progress-label"><strong>${escape(progressTitle)}</strong><span>${this.translated('progressOf', {count: p.total, next: milestone.next})}</span></div><progress value="${p.total}" max="${milestone.next}" aria-label="${this.translated('progressAria', {next: milestone.next})}">${Math.round(milestone.percent)}%</progress><p>${progressText}</p></div>
          ${p.upcoming.length ? `<section class="upcoming-section" aria-labelledby="passport-upcoming-heading"><h3 id="passport-upcoming-heading">${this.translated('upcomingHeading')}</h3><p class="upcoming-note">${this.translated('upcomingNote')}</p><div class="upcoming-grid">${p.upcoming.map(t => `<div class="upcoming-card"><span class="upcoming-status">${this.translated('bookedLabel')}</span><div class="stamp-head"><span>${escape(destinationName(t))}</span>${t.year === null ? '' : `<b>${t.year}</b>`}</div>${t.edition || t.year === null ? `<p>${escape(t.edition || t.name)}</p>` : ''}</div>`).join('')}</div></section>` : ''}
          ${p.badges.length ? `<h3>${this.translated('clubBadges', {club: this.data.milestones.clubName})}</h3><div class="earned-badges">${p.badges.map(b => `<figure class="earned-badge">${this.milestoneSeal(b)}<figcaption><strong>${escape(b.name)}</strong><span class="sr-only">, ${b.count} ${this.translated('retreatsLabel')}</span><p>${escape(b.message)}</p></figcaption></figure>`).join('')}</div>` : ''}
          ${p.reunions.length ? `<h3>${this.translated('reunionBadges')}</h3><p class="reunion-note">${this.translated(p.reunions.length === 1 ? 'reunionNoteOne' : 'reunionNoteMany', {count: p.reunions.length})}</p><div class="earned-badges reunion-collection">${p.reunions.map(r => `<figure class="earned-badge reunion-badge">${this.reunionSeal(r)}<figcaption><strong>${escape(r.badgeName)}</strong><p>${escape(r.name)}</p></figcaption></figure>`).join('')}</div>` : ''}
          ${p.trips.length ? `<h3>${this.translated('journeyHeading')}</h3>` : ''}
          <div class="stamps">${p.retreats.map(t => `<div class="stamp"><div class="stamp-head"><span>${escape(destinationName(t))}</span>${t.year === null ? '' : `<b>${t.year}</b>`}</div>${t.edition || t.year === null ? `<p>${escape(t.edition || t.name)}</p>` : ''}</div>`).join('')}</div>
          <a class="primary" href="${escape(this.retreatsUrl)}">${this.translated('findRetreat')}</a>
        </div>`;
      const dialog = this.shadowRoot.querySelector('dialog');
      dialog.showModal();
      dialog.scrollTop = 0;
    }
  }
  customElements.define('gs-retreat-passport', GSPassport);
})();
